<template>
    <div class="auth-wrapper auth-v1">
        <div class="auth-inner">
            <vue-element-loading :active="submiting" />
            <v-card class="auth-card">
                <!-- logo -->
                <v-card-title class="d-flex align-center justify-center py-7">
                    <v-img
                        :src="require('@/assets/images/logos/logo.png')"
                        max-height="30px"
                        max-width="30px"
                        alt="logo"
                        contain
                        class="me-3"
                    ></v-img>

                    <h2 class="text-2xl font-weight-semibold">
                        {{ systemName }}
                    </h2>
                </v-card-title>

                <!-- title -->
                <v-card-text>
                    <p class="font-weight-semibold text--primary mb-2">
                        Verify Your Phone Number
                    </p>
                    <p class="mb-2 text-center">
                        We've sent a 6-digit verification code to your phone number. Please enter the code below to complete your registration.
                    </p>
                </v-card-text>

                <!-- OTP form -->
                <v-card-text>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <!-- OTP Input Field -->
                        <div class="otp-container mb-4">
                            <p class="text-center mb-3 text-caption">
                                Enter the 6-digit code sent to your phone
                            </p>
                            <div class="otp-inputs d-flex justify-center mb-4">
                                <v-text-field
                                    v-model="digit1"
                                    ref="otpInput1"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(1, $event)"
                                    @keydown="handleKeyDown(1, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                                <v-text-field
                                    v-model="digit2"
                                    ref="otpInput2"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(2, $event)"
                                    @keydown="handleKeyDown(2, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                                <v-text-field
                                    v-model="digit3"
                                    ref="otpInput3"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(3, $event)"
                                    @keydown="handleKeyDown(3, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                                <v-text-field
                                    v-model="digit4"
                                    ref="otpInput4"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(4, $event)"
                                    @keydown="handleKeyDown(4, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                                <v-text-field
                                    v-model="digit5"
                                    ref="otpInput5"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(5, $event)"
                                    @keydown="handleKeyDown(5, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                                <v-text-field
                                    v-model="digit6"
                                    ref="otpInput6"
                                    outlined
                                    dense
                                    single-line
                                    hide-details
                                    maxlength="1"
                                    class="otp-digit mx-1"
                                    style="max-width: 60px"
                                    @input="handleInput(6, $event)"
                                    @keydown="handleKeyDown(6, $event)"
                                    @paste="handlePaste"
                                    :rules="[(v) => /^\d$/.test(v) || '']"
                                />
                            </div>
                        </div>

                        <!-- Error Message -->
                        <v-alert
                            v-if="errorMessage"
                            type="error"
                            dense
                            class="mb-4"
                        >
                            {{ errorMessage }}
                        </v-alert>

                        <!-- Success Message -->
                        <v-alert
                            v-if="successMessage"
                            type="success"
                            dense
                            class="mb-4"
                        >
                            {{ successMessage }}
                        </v-alert>


                        <!-- Verify Button -->
                        <v-btn
                            block
                            color="primary"
                            class="mt-6"
                            @click="submitOtp"
                            :disabled="!isOtpComplete || submiting"
                            :loading="submiting"
                            large
                        >
                            {{ submiting ? 'Verifying...' : 'Verify & Continue' }}
                        </v-btn>
                    </v-form>
                </v-card-text>

                <!-- Back to register link -->
                <v-card-text class="d-flex align-center justify-center flex-wrap mt-2">
                    <span class="me-2">Wrong phone number?</span>
                    <router-link :to="{ name: 'register' }">
                        Go back to registration
                    </router-link>
                </v-card-text>

                <!-- Help Section -->
                <v-card-text class="d-flex align-center justify-center flex-wrap">
                    <span class="me-2">Need help?</span>
                    <a href="#" @click="adminInfoDialog = true">
                        Contact Us
                    </a>
                </v-card-text>
            </v-card>

            <!-- Contact Us Dialog -->
            <v-row justify="center">
                <v-dialog
                    v-model="adminInfoDialog"
                    persistent
                    max-width="600px"
                >
                    <v-card>
                        <v-card-title>
                            <span class="text-h5">Contact Us</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        Email:
                                        <a
                                            v-bind="{
                                                href:
                                                    'mailto:' + adminInfo.email,
                                            }"
                                        >
                                            {{ adminInfo.email }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        Phone:
                                        <a
                                            v-bind="{
                                                href: 'tel:' + adminInfo.phone,
                                            }"
                                        >
                                            {{ adminInfo.phone }}
                                        </a>
                                    </v-col>
                                    <v-col cols="12">
                                        Address:
                                        <span>{{ adminInfo.address }}</span>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="blue darken-1"
                                text
                                @click="adminInfoDialog = false"
                            >
                                Close
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-row>
        </div>

        <!-- background triangle shape -->
        <img
            class="auth-mask-bg"
            height="190"
            :src="
                require(`@/assets/images/misc/mask-${
                    $vuetify.theme.dark ? 'dark' : 'light'
                }.png`)
            "
        />

        <!-- school image -->
        <v-img
            class="auth-tree my-5"
            width="247"
            height="185"
            src="@/assets/images/misc/school.png"
        ></v-img>

        <!-- school bus image -->
        <v-img
            class="auth-tree-3"
            width="400"
            height="250"
            src="@/assets/images/misc/school-bus-side-view.png"
        ></v-img>
    </div>
</template>

<script>
import { Keys } from "/src/config.js";
import VueElementLoading from "vue-element-loading";
import AuthService from "@/services/AuthService";

export default {
    components: {
        VueElementLoading,
    },
    data() {
        return {
            systemName: Keys.VUE_APP_SYSTEM_NAME,
            valid: true,
            submiting: false,
            digit1: '',
            digit2: '',
            digit3: '',
            digit4: '',
            digit5: '',
            digit6: '',
            errorMessage: null,
            successMessage: null,
            resendTimer: 0,
            adminInfo: {
                email: "",
                phone: "",
                address: "",
            },
            adminInfoDialog: false,
        };
    },
    computed: {
        otp() {
            return this.digit1 + this.digit2 + this.digit3 + this.digit4 + this.digit5 + this.digit6;
        },
        isOtpComplete() {
            return this.digit1 !== '' && this.digit2 !== '' && 
                   this.digit3 !== '' && this.digit4 !== '' &&
                   this.digit5 !== '' && this.digit6 !== '' &&
                   /^\d$/.test(this.digit1) && /^\d$/.test(this.digit2) &&
                   /^\d$/.test(this.digit3) && /^\d$/.test(this.digit4) &&
                   /^\d$/.test(this.digit5) && /^\d$/.test(this.digit6) ;
        }
    },
    mounted() {
        this.loadAdminInfo();
        // Focus on first input
        this.$nextTick(() => {
            if (this.$refs.otpInput1) {
                this.$refs.otpInput1.focus();
            }
        });
    },
    methods: {
        loadAdminInfo() {
            axios
                .get(`/users/admin-info`)
                .then((response) => {
                    this.adminInfo.email = response.data.admin_info.email;
                    this.adminInfo.phone = response.data.admin_info.phone;
                    this.adminInfo.address = response.data.admin_info.address;
                })
                .catch((error) => {
                    console.error("Error loading admin info:", error);
                });
        },
        handleInput(position, value) {
            // Clear error when user starts typing
            this.errorMessage = null;
            
            // Only allow digits
            if (value && !/^\d$/.test(value)) {
                this[`digit${position}`] = '';
                return;
            }
            
            // Move to next input if current is filled
            if (value) {
                if (position === 1 && this.$refs.otpInput2) {
                    this.$refs.otpInput2.focus();
                } else if (position === 2 && this.$refs.otpInput3) {
                    this.$refs.otpInput3.focus();
                } else if (position === 3 && this.$refs.otpInput4) {
                    this.$refs.otpInput4.focus();
                }else if  (position === 4 && this.$refs.otpInput5) {
                    this.$refs.otpInput5.focus();
                }else if  (position === 5 && this.$refs.otpInput6) {
                    this.$refs.otpInput6.focus();
                }
            }
        },
        handleKeyDown(position, event) {
            // Handle backspace
            if (event.key === 'Backspace' && !this[`digit${position}`] && position > 1) {
                const prevInput = this.$refs[`otpInput${position - 1}`];
                if (prevInput) {
                    prevInput.focus();
                    this[`digit${position - 1}`] = '';
                }
            }
            
            // Handle arrow keys
            if (event.key === 'ArrowLeft' && position > 1) {
                const prevInput = this.$refs[`otpInput${position - 1}`];
                if (prevInput) {
                    prevInput.focus();
                }
            }
            if (event.key === 'ArrowRight' && position < 6) {
                const nextInput = this.$refs[`otpInput${position + 1}`];
                if (nextInput) {
                    nextInput.focus();
                }
            }
        },
        handlePaste(event) {
            event.preventDefault();
            const pastedData = event.clipboardData.getData('text');
            const digits = pastedData.replace(/\D/g, '').split('').slice(0, 6);
            
            if (digits.length >= 1) this.digit1 = digits[0] || '';
            if (digits.length >= 2) this.digit2 = digits[1] || '';
            if (digits.length >= 3) this.digit3 = digits[2] || '';
            if (digits.length >= 4) this.digit4 = digits[3] || '';
            if (digits.length >= 5) this.digit5 = digits[4] || '';
            if (digits.length >= 6) this.digit6 = digits[5] || '';
            
            
            // Focus on the appropriate field
            if (digits.length === 6) {
                this.$refs.otpInput6.focus();
            } else if (digits.length > 0) {
                const nextPosition = Math.min(digits.length + 1, 6);
                this.$refs[`otpInput${nextPosition}`].focus();
            }
        },
        async submitOtp() {
            try {
                if (!this.isOtpComplete) {
                    this.errorMessage = "Please enter the complete 6-digit code";
                    return;
                }

                this.submiting = true;
                this.errorMessage = null;

                const result = await AuthService.verifyOtp({
                    uid: this.$route.query.uid,
                    otp: this.otp,
                    notify: this.$notify,
                });

                if (result) {
                    const isAdministrator = result.role_id == 1;
                    const isSchoolAdmin = result.role_id == 2;

                    this.successMessage = "OTP verified successfully!";
                    
                    this.$notify({
                        title: "Success",
                        text: "OTP verified! Redirecting...",
                        type: "success",
                    });

                    setTimeout(() => {
                        this.$router.push(
                            this.$router.currentRoute.query.to ||
                                (isAdministrator
                                    ? "/admin-dashboard"
                                    : isSchoolAdmin
                                    ? "/school-dashboard"
                                    : "/error-404")
                        );
                    }, 1000);
                } else {
                    this.errorMessage = "Invalid OTP. Please check the 6-digit code and try again.";
                }
            } catch (error) {
                this.errorMessage = error.response?.data?.message || 
                                  error.response?.data?.error || 
                                  error.message || 
                                  "Verification failed. Please try again.";
                
                this.$notify({
                    title: "Verification Error",
                    text: this.errorMessage,
                    type: "error",
                });
            } finally {
                this.submiting = false;
            }
        },
        async resendOtp() {
            try {
                this.submiting = true;
                this.errorMessage = null;

                // Call resend OTP API
                await AuthService.resendOtp({
                    uid: this.$route.query.uid,
                    notify: this.$notify,
                });

                this.$notify({
                    title: "Code Sent",
                    text: "A new 6-digit verification code has been sent to your phone.",
                    type: "success",
                });

                // Start countdown timer
                this.resendTimer = 60;
                const countdown = setInterval(() => {
                    this.resendTimer--;
                    if (this.resendTimer <= 0) {
                        clearInterval(countdown);
                    }
                }, 1000);

                // Clear OTP inputs
                this.digit1 = '';
                this.digit2 = '';
                this.digit3 = '';
                this.digit4 = '';
                this.digit5 = '';
                this.digit6 = '';
                
                // Focus on first input
                this.$nextTick(() => {
                    if (this.$refs.otpInput1) {
                        this.$refs.otpInput1.focus();
                    }
                });

            } catch (error) {
                this.errorMessage = "Failed to resend code. Please try again.";
                this.$notify({
                    title: "Resend Failed",
                    text: this.errorMessage,
                    type: "error",
                });
            } finally {
                this.submiting = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "~@/plugins/vuetify/default-preset/preset/pages/auth.scss";

.otp-inputs {
    gap: 12px;
}

.otp-digit {
    text-align: center;
    
    ::v-deep .v-input__control {
        .v-input__slot {
            justify-content: center;
        }
        
        .v-text-field__slot input {
            text-align: center;
            font-size: 1.8rem;
            font-weight: bold;
            color: var(--v-primary-base);
        }
    }
    
    ::v-deep .v-text-field.v-text-field--outlined .v-input__control .v-input__slot {
        border-radius: 12px;
    }
    
    &:focus-within {
        ::v-deep .v-text-field--outlined .v-input__slot {
            border-color: var(--v-primary-base) !important;
            border-width: 2px;
        }
    }
}

.otp-container {
    .text-caption {
        color: rgba(var(--v-theme-on-surface), 0.7);
    }
}

// Animation for success state
.v-alert.success {
    animation: slideInDown 0.3s ease-out;
}

@keyframes slideInDown {
    from {
        transform: translateY(-10px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

// Responsive adjustments
@media (max-width: 600px) {
    .otp-digit {
        max-width: 50px !important;
        margin: 0 4px !important;
    }
    
    .otp-inputs {
        gap: 8px;
    }
}
</style>